import { Routes, Route } from "react-router-dom";
import { useRef, useState, useEffect } from "react";
import "font-awesome/css/font-awesome.min.css";

// ROUTING PERMISSIONS
import ProtectedRoute from "./routes/ProtectedRoutes";
import PublicRoute from "./routes/PublicRoutes";

// LAYOUTS
import MainLayout from "./layouts/AyurThemeLayout";
import UserPanelLayout from "./layouts/UserPanelLayout.js";

import Shop from "./pages/Shop";
import Cart from "./pages/Cart";
import Home from "./pages/Home";
import Login from "./pages/Login";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Register from "./pages/Register";
import Services from "./pages/Services";
import Team from "./pages/Team";
import ProductDetail from "./pages/DetailedProduct.js";
import DetailedService from "./pages/DetailedService";
import TherapiesTreatments from "./pages/TherapiesTreatments";
import USHAKriyaWellness from "./pages/USHAKriyaWellness";
import Blog from "./pages/Blog";
import DetailedBlog from "./pages/DetailedBlog";
import DetailedTreatments from "./pages/DetailedTreatments";
import DharaWellnessProgram from "./pages/DharaWellnessProgram";
import MainCategory from "./pages/MainCategory/MainCategory";
import SubCategory from "./pages/SubCategory/SubCategory";
// SIDE BAR COMPONENTS OR PAGES

// USER PANEL PAGES AND THEIR ROUTES DEFINED BELOW:
import Dashboard from "./pages/UserPanel/index.js";
import PatientAppointments from "./pages/UserPanel/PatientAppointments";
import Doctors from "./pages/UserPanel/Doctors";
import Prescriptions from "./pages/UserPanel/Prescriptions";
import Invoice from "./pages/UserPanel/Invoice";
import ProfileSettings from "./pages/UserPanel/ProfileSettings";
import ChangePassword from "./pages/UserPanel/ChangePassword";
import Notifications from "./pages/UserPanel/NotificationSettings.js";
import DetailedPescription from "./pages/UserPanel/DetailedPescription.js";

import { getApiBaseUrl } from "./config/Production";
//CSS
import "./App.css";

// IMAGES FOR ALL THE SERVICES
import singleImg08 from "./assets/AyurvedaAssets/images/single-img-08.png";
import Service01 from "./assets/AyurvedaAssets/images/services/service-01.png";
import Service02 from "./assets/AyurvedaAssets/images/services/service-02.png";
import Service03 from "./assets/AyurvedaAssets/images/services/service-03.png";
import Service04 from "./assets/AyurvedaAssets/images/services/service-04.png";
import Service05 from "./assets/AyurvedaAssets/images/services/service-05.jpg";
import Service06 from "./assets/AyurvedaAssets/images/services/service-06.jpg";
import OurCenters from "./pages/OurCenters.js";
import DetailedInvoices from "./pages/UserPanel/DetailedInvoices.js";
import NotificationSettings from "./pages/UserPanel/NotificationSettings.js";

function App() {
  const menuWrapperRef = useRef(null);
  const menuContentRef = useRef(null);
  // AYURVEDIC STATES
  const [serviceLinks, setServiceLinks] = useState([]);
  const [TreatmentLinks, setTreatmentLinks] = useState([]);
  // GLOBAL STATES
  const [Production, setProduction] = useState(false);
  // USER PANEL STATES
  const [SideBarComponents, setSideBarComponents] = useState({});
  const [SideBar, setSideBar] = useState([]);
  // CONST PRODUCTS AND THEIR PAGES AND ROUTES DYNAMICALLY

  // DYNAMIC SERVICE LISTING (Change Only Here To Change Any Service Data)
  const AllServicesListing = [
    {
      id: 8,
      url: "/Dhara-Wellness-Program",
      name: "Dhara Wellness Program",
      image: [Service06],
      serviceListingDesc:
        "Four structured, mentor-led programs: 90-Day Weight Management, 90-Day Pain Relief, 90-Day Diabetes Care, and Panchakarma (7/11/15-day In-Center) — each combining Daily Usha Kriya, diet discipline, targeted herbs, and periodic reviews.",
      description:
        "Discipline + nature-aligned routine = sustained improvement. Choose a focused 90-day path or a short in-center Panchakarma detox.",
      detailedPageTopDescription:
        "Soil to Soul — Natural Farming × Ayurveda. अनुशासन + प्रकृति-सम्मत दिनचर्या = स्थायी सुधार.",
      steps: [],
      pricing: [],
    },
    {
      id: 1,
      url: "/detailed-consultation-&-diagnostics-process",
      name: "Detailed Consultation & Diagnostics Process",
      image: [Service01],
      serviceListingDesc:
        "Initial Ayurvedic Consultation – A complete 18-point assessment through pulse reading, lifestyle review, and classical Ashtavidha–Dashavidha examination to identify your Prakriti and current imbalances, leading to a personalized plan of diet, lifestyle, Auṣadhi (herbal medicines), and therapies.",
      description:
        "Step-by-step process that guides you through our holistic healing journey.",
      detailedPageTopDescription: `Ayurveda believes every person is unique. As the sages said: 
        <b>“Correct diagnosis is half the cure.”</b>
        
        At Bharat Dhara, we combine ancient Ayurvedic wisdom from Charaka, Sushruta, Vagbhata, and others —
        along with modern health checks — to understand your body and mind fully before beginning treatment.
        
        Unlike quick symptom-based consultations, our process is about knowing you completely: 
        your body, mind, lifestyle, and natural constitution. Below is the detailed flow we follow at Bharat Dhara.
      `,
      steps: [
        {
          step: 1,
          title: "Registration & Intake (Pravesh)",
          subtitle: "Step 1. Registration & Intake (Pravesh)",
          image: singleImg08, // imported image asset
          collectHead: "What we collect",
          collect: [
            { label: "Basic details", value: "age, gender, occupation" },
            {
              label: "Lifestyle habits",
              value: "diet, sleep, stress, exercise, addictions",
            },
            {
              label: "Health history",
              value: "past illnesses & current treatments",
            },
          ],
          matters: {
            heading: "Why this matters",
            description: `
              <p>
                This gives us a 
                <span class="highlight">complete picture of your health</span> 
                — not just symptoms. It helps identify your 
                <b>natural constitution (Prakriti)</b>, current 
                <b>imbalances (Vikriti)</b>, and guides a plan
                that fits your body, mind, and lifestyle.
              </p>
            `,
          },
        },
        {
          step: 2,
          title: "Primary Discussion (Prashna Pariksha)",
          subtitle: "Step 2. Primary Discussion (Prashna Pariksha)",
          image: singleImg08,
          collectHead: "What we ask",
          collect: [
            {
              label: "Complaints",
              value:
                "what brings you here (pain, digestion, skin, stress, etc.)",
            },
            {
              label: "History",
              value: "when did it start, what worsens/relieves it",
            },
            {
              label: "Family tendencies",
              value: "diabetes, hypertension, allergies, etc.",
            },
            {
              label: "Mental health",
              value: "mood, memory, emotional stability",
            },
          ],
          matters: {
            heading: "Why this matters",
            description: `
              <p>
                This helps us trace the 
                <span class="highlight">root of your health issues</span>,
                not just symptoms. Patterns in history and family tendencies 
                reveal deeper imbalances. <br><br>
                👉 Here we already begin to see <b>dosha tendencies</b>:
                <span class="highlight"> anxiety → Vata</span>,
                <span class="highlight"> anger → Pitta</span>,
                <span class="highlight"> laziness → Kapha</span>.
              </p>
            `,
          },
        },
        {
          step: 3,
          title: "Visual Observation (Darshana)",
          subtitle: "Step 3. Visual Observation (Darshana)",
          image: singleImg08,
          collectHead: "What we check",
          collect: [
            {
              label: "Skin",
              value: "dry/oily, color, natural glow",
            },
            {
              label: "Eyes",
              value: "clarity, redness, dullness",
            },
            {
              label: "Tongue",
              value: "color, coating, cracks",
            },
            {
              label: "Body posture & walk:",
              value: "steady, restless, heavy",
            },
            {
              label: "Nails & hair:",
              value: "brittle, shiny, dull",
            },
          ],
          matters: {
            heading: "Why this matters",
            description: `
              <p>
                Visual signs are the
                <span className="highlight">
                  first indicators
                </span>
                of inner imbalance. For example: <br></br>
                <br></br>
                🌀 Dry, cracked skin →
                <span className="highlight">Vata</span>
                <br></br>
                🔥 Red, inflamed eyes →
                <span className="highlight">Pitta</span>
                <br></br>
                🌿 Oily, heavy skin →
                <span className="highlight">Kapha</span>
                <br></br>
                <br></br>
                These observations help refine diagnosis before
                deeper tests.
              </p>
            `,
          },
        },
        {
          step: 4,
          title: "Pulse & Touch Examination (Nadi & Sparshana)",
          subtitle: "Step 4. Pulse & Touch Examination (Nadi & Sparshana)",
          image: singleImg08,
          collectHead: "What we check",
          collect: [
            {
              label: "Nadi Pariksha (Pulse)",
              value: "primary tool for dosha assessment",
            },
            {
              label: "Vata pulse",
              value: "irregular, fast, snake-like",
            },
            {
              label: "Pitta pulse",
              value: "sharp, forceful, frog-like",
            },
            {
              label: "Kapha pulse",
              value: "slow, steady, swan-like",
            },
            {
              label: "Touch (temperature)",
              value: "cold = Vata, hot = Pitta, moist = Kapha",
            },
            {
              label: "Palpation",
              value: "swelling, tenderness, organ/joint condition",
            },
          ],
          matters: {
            heading: "Why this matters",
            description: `
            <p>
              Pulse qualities and touch findings provide a
              <span className="highlight">
                real-time map of doshic activity
              </span>
              . They help distinguish whether issues are
              <b>Vata (movement/nerve)</b>,
              <b> Pitta (heat/inflammation)</b>, or
              <b> Kapha (fluid/stability)</b> dominant, so
              treatments can be targeted—
              <b>right herb, right therapy, right intensity</b>.
            </p>
            `,
          },
        },
        {
          step: 5,
          title: "Eightfold Examination (Ashtasthana Pariksha)",
          subtitle: "Step 5. Eightfold Examination (Ashtasthana Pariksha)",
          image: singleImg08,
          collectHead: "What we check",
          collect: [
            {
              label: "Pulse (Nadi)",
              value: "",
            },
            {
              label: "Urine (Mutra):",
              value: "frothy, cloudy, reddish signs",
            },
            {
              label: "Stool (Mala):",
              value: "constipation, diarrhea, stickiness",
            },
            {
              label: "Tongue (Jihva):",
              value: "white coat = Kapha, yellow = Pitta, black/dry = Vata",
            },
            {
              label: "Voice (Shabda):",
              value: "weak, harsh, or strong",
            },
            {
              label: "Touch/Temperature (Sparsha)",
              value: "",
            },
            {
              label: "Eyes (Drik)",
              value: "",
            },
            {
              label: "Body shape (Akruti)",
              value: "",
            },
          ],
          matters: {
            heading: "Why this matters",
            description: `
            <p>
              The <b>Eightfold Examination</b> combines external
              and internal markers to assess
              <span className="highlight">
                dosha balance
              </span>
              and
              <span className="highlight">
                Ama (toxin load)
              </span>
              . For example, tongue coating may reveal
              <b>Kapha Ama</b>, while yellow discoloration
              points to <b>Pitta excess</b>. 👉 By integrating
              all eight signs, the vaidya gains a
              <b>360° diagnostic view</b> of your health.
            </p>
            `,
          },
        },
        {
          step: 6,
          title: "Tenfold Examination (Dashavidha Pariksha)",
          subtitle: "Step 6. Tenfold Examination (Dashavidha Pariksha)",
          image: singleImg08,
          collectHead: "What we map",
          collect: [
            {
              label: "Prakriti:",
              value: "in-born Vata–Pitta–Kapha balance",
            },
            {
              label: "Vikruti:",
              value: "current disturbance/imbalance",
            },
            {
              label: "Sara:",
              value:
                "tissue quality (blood, muscle, fat, bone, marrow, semen/ojas)",
            },
            {
              label: "Samhanana:",
              value: "body build (compact/strong/weak)",
            },
            {
              label: "Pramana:",
              value: "proportions & measurements (height/weight/frames)",
            },
            {
              label: "Satmya:",
              value: "adaptability—foods/regimens that suit you",
            },
            {
              label: "Satva:",
              value: "mental strength—confidence, memory, fears",
            },
            {
              label: "Ahara Shakti:",
              value: "digestive power/appetite & assimilation",
            },
            {
              label: "Vyayama Shakti:",
              value: "exercise capacity/tolerance",
            },
            {
              label: "Vaya:",
              value: "age & life stage considerations",
            },
          ],
          matters: {
            heading: "Why this matters",
            description: `
            <p>
              The tenfold lens creates a
              <span className="highlight">
                precise health profile
              </span>
              that guides the <b>right diet</b> (what to
              favor/avoid), <b>correct therapies</b> and oils,
              <b> dosage & herb selection</b>, and a suitable
              <b>exercise plan</b>. It aligns treatment with
              your <b>constitution, capacity, and life stage</b>
              —making care safer, targeted, and effective.
            </p>
            `,
          },
        },
        {
          step: 7,
          title: "Root-Cause Analysis (Nidana Panchaka)",
          subtitle: "Step 7: Root-Cause Analysis (Nidana Panchaka)",
          image: singleImg08,
          collectHead: "What we check",
          collect: [
            {
              label: "Nidana (Cause):",
              value: "food choices, habits, work/rest patterns, stress factors",
            },
            {
              label: "Purvarupa (Early signs):",
              value: "subtle warnings before full disease appears",
            },
            {
              label: "Rupa (Symptoms):",
              value: "the clear, present features of the condition",
            },
            {
              label: "Upashaya:",
              value: "what <i>relieves</i> or <i>aggravates</i> the complaint",
            },
            {
              label: "Samprapti (Path of disease):",
              value: "how it starts, spreads, and settles in tissues",
            },
          ],
          matters: {
            heading: "Why this matters",
            description: `
            <p>
              This five-step analysis reveals the{" "}
              <span className="highlight">true driver</span> of
              illness. By tracing triggers and the disease
              pathway, we can remove the cause (
              <b>Nidana Parivarjana</b>), choose the right
              therapies, and prevent relapse—moving from symptom
              control to
              <b> root-level healing</b>.
            </p>
            `,
          },
        },
        {
          step: 8,
          title: "Stage of Disease (Shatkriyakala)",
          subtitle: "Step 8: Stage of Disease (Shatkriyakala)",
          image: singleImg08,
          collectHead: "What we assess",
          collect: [
            {
              label: "1. Sanchaya (Accumulation):",
              value:
                "doshas starting to collect — ideal time for diet & lifestyle correction",
            },
            {
              label: "2. Prakopa (Aggravation):",
              value: "doshas getting provoked, symptoms becoming clearer",
            },
            {
              label: "3. Prasara (Spread):",
              value: "doshas leaving their main seat and moving to other areas",
            },
            {
              label: "4. Sthanasamshray (Localization):",
              value: "doshas + weak tissue = disease seed — high risk stage",
            },
            {
              label: "5. Vyakti (Manifestation):",
              value: "full-blown clinical picture, clear diagnosis possible",
            },
            {
              label: "6. Bheda (Complication):",
              value:
                "chronicity, complications, mixed dosha/tissue involvement",
            },
          ],
          matters: {
            heading: "Why this matters",
            description: `
      <p>
        Knowing <b>which stage</b> your illness is in tells us <b>how aggressive</b>
        the treatment must be. Early stages respond to <span className="highlight">
        diet, routine, and simple detox</span>; later stages need <b>Panchakarma,
        specific medicines, and Rasayana</b> to reverse damage and prevent relapse.
      </p>
    `,
          },
        },
        {
          step: 9,
          title: "Srotas, Agni & Ojas Check",
          subtitle: "Step 9: Srotas, Agni & Ojas Check",
          image: singleImg08,
          collectHead: "What we check",
          collect: [
            {
              label: "Srotas (Body channels):",
              value:
                "annavaha, raktavaha, pranavaha, manovaha — flow vs blockage",
            },
            {
              label: "Agni (Digestive fire):",
              value:
                "sama (balanced), manda (weak), vishama/tikshna (irregular/sharp)",
            },
            {
              label: "Ama (Toxins):",
              value: "bloating, heaviness, coated tongue, bad breath",
            },
            {
              label: "Ojas (Vitality):",
              value: "immunity, energy, enthusiasm, sound sleep, resilience",
            },
          ],
          matters: {
            heading: "Why this matters",
            description: `
      <p>
        These four tell us how you <b>digest, circulate, detoxify</b> and
        <b> preserve vitality</b>. If Agni is low → we do <b>Deepana–Pachana</b>.
        If Srotas are clogged/Ama high → we do <b>Langhana, Svedana, Shodhana</b>.
        If Ojas is low → we add <b>Rasayana + nourishment + rest</b>.
      </p>
    `,
          },
        },
        {
          step: 10,
          title: "Prognosis (Future Outlook)",
          subtitle: "Step 10: Prognosis (Future Outlook)",
          image: singleImg08,
          collectHead: "What we tell you",
          collect: [
            {
              label: "Easily curable:",
              value: "early stage, good Agni & Ojas, high compliance",
            },
            {
              label: "Difficult but treatable:",
              value: "needs intensive plan + close monitoring",
            },
            {
              label: "Long-term management:",
              value:
                "chronic disease, organ/tissue involvement, relapse tendency",
            },
            {
              label: "Supportive care only:",
              value: "focus on strength, comfort, quality of life",
            },
          ],
          matters: {
            heading: "Why this matters",
            description: `
      <p>
        We set <b>clear expectations</b> based on your stage (Shatkriyakala),
        dosha load, Agni–Ojas, chronicity and lifestyle capacity.
        This keeps the plan <b>honest, achievable, and trackable</b>.
      </p>
    `,
          },
        },
        {
          step: 11,
          title: "Modern Supportive Checks",
          subtitle: "Step 11: Modern Supportive Checks",
          image: singleImg08,
          collectHead: "What we add (when needed)",
          collect: [
            {
              label: "Vitals:",
              value: "BP, sugar, BMI, pulse, SpO₂",
            },
            {
              label: "Lab tests:",
              value:
                "CBC, lipid profile, LFT, RFT, TFT — to rule out red flags",
            },
            {
              label: "Devices / scans:",
              value:
                "wellness/energy scan for trends (supportive, not diagnostic)",
            },
            {
              label: "Digital records:",
              value:
                "PDF report, follow-up tracker, tele-consult friendly notes",
            },
          ],
          matters: {
            heading: "Why this matters",
            description: `
      <p>
        These tools <b>support, not replace</b> Ayurveda. They give a
        <b>safe baseline</b>, help monitor progress, and make it easy to
        review in camps/online consults.
      </p>
    `,
          },
        },
        {
          step: 12,
          title: "Final Guidance & Report",
          subtitle: "Step 12: Final Guidance & Report",
          image: singleImg08,
          collectHead: "What you receive",
          collect: [
            {
              label: "Aahar (Food):",
              value: "dosha-wise diet, avoid list, timing",
            },
            {
              label: "Vihar (Lifestyle):",
              value: "dinacharya, ritucharya, sleep, stress, exercise",
            },
            {
              label: "Aushadhi (Medicines):",
              value: "name, dose, anupana, duration",
            },
            {
              label: "Chikitsa & Rasayana:",
              value: "Panchakarma/therapy plan + immunity/rejuvenation",
            },
          ],
          matters: {
            heading: "Why this matters",
            description: `
      <p>
        This is your <b>action sheet</b>. It turns diagnosis into
        <b>daily practice</b> so you can start immediately, stay consistent,
        and update on follow-up.
      </p>
    `,
          },
        },
        {
          step: 13,
          title: "Key Takeaway",
          subtitle: "Key Takeaway",
          image: singleImg08,
          collectHead: "What this 12-step flow ensures",
          collect: [
            {
              label: "Prakriti & Vikriti both:",
              value: "who you are by nature, and what is disturbed now",
            },
            {
              label: "Root & Stage known:",
              value: "why disease formed, and how far it has gone",
            },
            {
              label: "Personalized path:",
              value: "food + lifestyle + medicine + therapies",
            },
            {
              label: "Relapse prevention:",
              value: "Rasayana, follow-ups, education",
            },
          ],
          matters: {
            heading: "Why this matters",
            description: `
      <p>
        This is not a 5-minute consult. It’s a <b>classical, stepwise Ayurveda
        workflow</b> that respects tradition and still works in modern clinics.
        You get a <b>clear, culture-rooted, doable plan</b>.
      </p>
    `,
          },
        },
      ],
      pricing: [],
    },
    {
      id: 2,
      url: "/detox-rejuvenation",
      name: "Detox & Rejuvenation",
      image: [Service01],
      serviceListingDesc:
        "Preparatory phase of Panchkarma with Snehana (oleation), Abhyanga (oil massage), Shirodhara, Swedana, and other therapies to loosen toxins, balance doshas, and prepare the body for deep detoxification.",
      description:
        "A structured program to detoxify, balance, and rejuvenate your body and mind.",
      detailedPageTopDescription: "",
      steps: [
        {
          step: 1,
          title: "Panchkarma: Preparatory Therapies (पूर्वकर्म)",
          subtitle: "Step 1. Poorva Karma – Body preparation before detox",
          image: singleImg08,
          collectHead: "What we do / apply",
          collect: [
            {
              label: "Snehana (स्नेहन)",
              value: "internal & external oleation",
            },
            {
              label: "Swedana (स्वेदन)",
              value: "steam/sudation to liquefy & move doshas",
            },
            {
              label: "Bashpa Sweda",
              value: "steam bath / steam box – full body sudation",
            },
            {
              label: "Maddi Patti / Lep",
              value: "herbal poultice / mud pack for pain & detox",
            },
            {
              label: "Abhyanga",
              value: "full-body Ayurvedic oil massage",
            },
            {
              label: "Shirodhara variants",
              value: "jala, tila, ghrita, panchagavya, takra",
            },
            {
              label: "Local basti",
              value: "kati, janu, griva, nabhi, hridi (warm oil pooling)",
            },
            {
              label: "Supportive",
              value: "netra tarpana, pada abhyanga, hot/cold compress",
            },
          ],
          matters: {
            heading: "Why this matters",
            description: `
          <p>
            Poorva Karma <b>loosens and mobilizes toxins</b> from deeper tissues
            and <b>opens the srotas (channels)</b>. If this is weak, the main
            Panchkarma (Vamana/Virechana/Basti) will be harsh or less effective.
            That’s why Bharat Dhara protocol never skips this stage.
          </p>
        `,
          },
        },
        {
          step: 2,
          title: "Panchkarma: Main Therapies (प्रधानकर्म)",
          subtitle: "Step 2. Pradhan Karma – Core five detox procedures",
          image: singleImg08,
          collectHead: "What we select (as per dosha & indication)",
          collect: [
            {
              label: "Vamana (वमन)",
              value: "therapeutic emesis for Kapha disorders",
            },
            {
              label: "Virechana (विरेचन)",
              value: "purgation for Pitta / skin / liver issues",
            },
            {
              label: "Basti (बस्ति)",
              value: "Vata-pacifying medicated enema",
            },
            {
              label: "Basti types",
              value: "anuvasana (oil), niruha (decoction), yoga basti (combo)",
            },
            {
              label: "Nasya (नस्य)",
              value: "head–neck–sinus clearing, hormones, mind",
            },
            {
              label: "Raktamokshana (रक्तमोक्षण)",
              value: "bloodletting for localized dushta rakta conditions",
            },
          ],
          matters: {
            heading: "Why this matters",
            description: `
          <p>
            This is the <b>actual shodhana (elimination)</b> phase — where the
            doshas that were brought to the gut are expelled in a controlled,
            physician-led way. This is what gives Panchkarma its <b>deep,
            long-lasting results</b> compared to only massage/spa therapies.
          </p>
        `,
          },
        },
        {
          step: 3,
          title: "Panchkarma: Post Procedures (पश्चात्कर्म)",
          subtitle: "Step 3. Paschat Karma – Recovery, nourishment & reset",
          image: singleImg08,
          collectHead: "What we guide you through",
          collect: [
            {
              label: "Sansarjana Krama",
              value: "stepwise diet from liquid → semi-solid → normal",
            },
            {
              label: "Rasayana Chikitsa",
              value: "rejuvenation, ojas-building, anti-ageing herbs",
            },
            {
              label: "Lifestyle reset",
              value: "yoga, pranayama, meditation, dinacharya",
            },
            {
              label: "Follow-up plan",
              value: "when to repeat/balance, when to avoid heavy food",
            },
          ],
          matters: {
            heading: "Why this matters",
            description: `
          <p>
            After detox the body is <b>highly receptive</b> but also
            <b>temporarily vulnerable</b>. If we don’t rebuild Agni,
            tissues, and routine, results fade. Paschat Karma
            <b>locks the benefits</b> and prevents relapse.
          </p>
        `,
          },
        },
      ],
      pricing: [],
    },
    {
      id: 3,
      url: "/therapies-treatments",
      name: "Therapies & Treatments",
      image: [Service03],
      serviceListingDesc:
        "Experience classical Ayurvedic therapies including Abhyanga oil massage, Shirodhara, Basti, Nasya, Swedana herbal steam, Udvartana powder massage, Netra Basti, and Marma therapy — designed to balance doshas, relieve stress, detoxify, and rejuvenate body and mind.",
      description:
        "A structured program to detoxify, balance, and rejuvenate your body and mind.",
      detailedPageTopDescription:
        "A structured program to detoxify, balance, and rejuvenate your body and mind.",
      steps: [
        {
          step: 1,
          title: "Registration & Intake (Pravesh)",
          subtitle: "Step 1. Registration & Intake (Pravesh)",
          image: singleImg08, // imported image asset
          collectHead: "What we collect",
          collect: [
            { label: "Basic details", value: "age, gender, occupation" },
            {
              label: "Lifestyle habits",
              value: "diet, sleep, stress, exercise, addictions",
            },
            {
              label: "Health history",
              value: "past illnesses & current treatments",
            },
          ],
          matters: {
            heading: "Why this matters",
            description: `
              <p>
                This gives us a 
                <span class="highlight">complete picture of your health</span> 
                — not just symptoms. It helps identify your 
                <b>natural constitution (Prakriti)</b>, current 
                <b>imbalances (Vikriti)</b>, and guides a plan
                that fits your body, mind, and lifestyle.
              </p>
            `,
          },
        },
        {
          step: 2,
          title: "Primary Discussion (Prashna Pariksha)",
          subtitle: "Step 2. Primary Discussion (Prashna Pariksha)",
          image: singleImg08,
          collectHead: "What we ask",
          collect: [
            {
              label: "Complaints",
              value:
                "what brings you here (pain, digestion, skin, stress, etc.)",
            },
            {
              label: "History",
              value: "when did it start, what worsens/relieves it",
            },
            {
              label: "Family tendencies",
              value: "diabetes, hypertension, allergies, etc.",
            },
            {
              label: "Mental health",
              value: "mood, memory, emotional stability",
            },
          ],
          matters: {
            heading: "Why this matters",
            description: `
              <p>
                This helps us trace the 
                <span class="highlight">root of your health issues</span>,
                not just symptoms. Patterns in history and family tendencies 
                reveal deeper imbalances. <br><br>
                👉 Here we already begin to see <b>dosha tendencies</b>:
                <span class="highlight"> anxiety → Vata</span>,
                <span class="highlight"> anger → Pitta</span>,
                <span class="highlight"> laziness → Kapha</span>.
              </p>
            `,
          },
        },
      ],
    },
    {
      id: 4,
      url: "/wellness-support-offerings",
      name: "Wellness & Support Offerings",
      image: [Service04],
      serviceListingDesc:
        "A holistic blend of preventive and healing practices in Ayurveda, offering diet and lifestyle planning, yoga therapy, meditation, USHA Kriya, and herbal remedies to nurture balance of body, mind, and spirit.",
      description:
        "A structured program to detoxify, balance, and rejuvenate your body and mind.",
      detailedPageTopDescription: "",
      steps: [],
    },
    {
      id: 5,
      url: "/special-programs",
      name: "Special Programs",
      image: [Service05],
      serviceListingDesc:
        "Focused Ayurvedic care programs designed for modern concerns—offering stress and sleep improvement therapies like Shirodhara and meditation, along with pain management through basti, marma therapy, and herbal support.",
      description:
        "A structured program to detoxify, balance, and rejuvenate your body and mind.",
      detailedPageTopDescription: "",
      steps: [],
    },
    {
      id: 6,
      url: "/traditional-healers-ancient-wisdom",
      name: "Traditional Healers & Ancient Wisdom",
      image: [Service06],
      serviceListingDesc:
        "Experience age-old wellness rooted in Indian traditions—Panchgavya therapies, Desi Vaidya consultations, Agni Chikitsa, Gau Shala retreats, and simple home remedies that nurture immunity, balance, and spiritual harmony.",
      description:
        "A structured program to detoxify, balance, and rejuvenate your body and mind.",
      detailedPageTopDescription: "",
      steps: [],
    },
    // 👉 if you want Dhara to also appear in the Services grid,
    // add an id: 8 object here with url: "/Dhara-Wellness-Program"
  ];

  const SericesUrlLinks =
    AllServicesListing.length > 0
      ? AllServicesListing.map((item) => {
          return {
            id: item.id,
            url: item.url,
            name: item.name,
          };
        })
      : null;

  const ServicesNames =
    AllServicesListing.length > 0
      ? AllServicesListing.map((item) => {
          return {
            id: item.id,
            name: item.name,
          };
        })
      : null;
  // DYNAMIC TREATMENT LISTING
  const AllTreatmentListing = [
    {
      id: 1,
      url: "/holistic-&-auxiliary-therapies",
      name: "Holistic & Auxiliary Therapies",
      image: [Service01],
      serviceListingDesc:
        "Initial Ayurvedic Consultation – A complete 18-point assessment through pulse reading, lifestyle review, and classical Ashtavidha–Dashavidha examination to identify your Prakriti and current imbalances, leading to a personalized plan of diet, lifestyle, Auṣadhi (herbal medicines), and therapies.",
      description:
        "Step-by-step process that guides you through our holistic healing journey.",
      detailedPageTopDescription: `Ayurveda believes every person is unique. As the sages said: 
        <b>“Correct diagnosis is half the cure.”</b>
        
        At Bharat Dhara, we combine ancient Ayurvedic wisdom from Charaka, Sushruta, Vagbhata, and others —
        along with modern health checks — to understand your body and mind fully before beginning treatment.
        
        Unlike quick symptom-based consultations, our process is about knowing you completely: 
        your body, mind, lifestyle, and natural constitution. Below is the detailed flow we follow at Bharat Dhara.
      `,
      steps: [
        {
          step: 1,
          title: "Registration & Intake (Pravesh)",
          subtitle: "Step 1. Registration & Intake (Pravesh)",
          image: singleImg08, // imported image asset
          collectHead: "What we collect",
          collect: [
            { label: "Basic details", value: "age, gender, occupation" },
            {
              label: "Lifestyle habits",
              value: "diet, sleep, stress, exercise, addictions",
            },
            {
              label: "Health history",
              value: "past illnesses & current treatments",
            },
          ],
          matters: {
            heading: "Why this matters",
            description: `
              <p>
                This gives us a 
                <span class="highlight">complete picture of your health</span> 
                — not just symptoms. It helps identify your 
                <b>natural constitution (Prakriti)</b>, current 
                <b>imbalances (Vikriti)</b>, and guides a plan
                that fits your body, mind, and lifestyle.
              </p>
            `,
          },
        },
        {
          step: 2,
          title: "Primary Discussion (Prashna Pariksha)",
          subtitle: "Step 2. Primary Discussion (Prashna Pariksha)",
          image: singleImg08,
          collectHead: "What we ask",
          collect: [
            {
              label: "Complaints",
              value:
                "what brings you here (pain, digestion, skin, stress, etc.)",
            },
            {
              label: "History",
              value: "when did it start, what worsens/relieves it",
            },
            {
              label: "Family tendencies",
              value: "diabetes, hypertension, allergies, etc.",
            },
            {
              label: "Mental health",
              value: "mood, memory, emotional stability",
            },
          ],
          matters: {
            heading: "Why this matters",
            description: `
              <p>
                This helps us trace the 
                <span class="highlight">root of your health issues</span>,
                not just symptoms. Patterns in history and family tendencies 
                reveal deeper imbalances. <br><br>
                👉 Here we already begin to see <b>dosha tendencies</b>:
                <span class="highlight"> anxiety → Vata</span>,
                <span class="highlight"> anger → Pitta</span>,
                <span class="highlight"> laziness → Kapha</span>.
              </p>
            `,
          },
        },
        {
          step: 3,
          title: "Visual Observation (Darshana)",
          subtitle: "Step 3. Visual Observation (Darshana)",
          image: singleImg08,
          collectHead: "What we check",
          collect: [
            {
              label: "Skin",
              value: "dry/oily, color, natural glow",
            },
            {
              label: "Eyes",
              value: "clarity, redness, dullness",
            },
            {
              label: "Tongue",
              value: "color, coating, cracks",
            },
            {
              label: "Body posture & walk:",
              value: "steady, restless, heavy",
            },
            {
              label: "Nails & hair:",
              value: "brittle, shiny, dull",
            },
          ],
          matters: {
            heading: "Why this matters",
            description: `
              <p>
                Visual signs are the
                <span className="highlight">
                  first indicators
                </span>
                of inner imbalance. For example: <br></br>
                <br></br>
                🌀 Dry, cracked skin →
                <span className="highlight">Vata</span>
                <br></br>
                🔥 Red, inflamed eyes →
                <span className="highlight">Pitta</span>
                <br></br>
                🌿 Oily, heavy skin →
                <span className="highlight">Kapha</span>
                <br></br>
                <br></br>
                These observations help refine diagnosis before
                deeper tests.
              </p>
            `,
          },
        },
        {
          step: 4,
          title: "Pulse & Touch Examination (Nadi & Sparshana)",
          subtitle: "Step 4. Pulse & Touch Examination (Nadi & Sparshana)",
          image: singleImg08,
          collectHead: "What we check",
          collect: [
            {
              label: "Nadi Pariksha (Pulse):",
              value: "primary tool for dosha assessment",
            },
            {
              label: "Vata pulse:",
              value: "irregular, fast, snake-like",
            },
            {
              label: "Pitta pulse:",
              value: "sharp, forceful, frog-like",
            },
            {
              label: "Kapha pulse:",
              value: "slow, steady, swan-like",
            },
            {
              label: "Touch (temperature):",
              value: "cold = Vata, hot = Pitta, moist = Kapha",
            },
            {
              label: "Palpation:",
              value: "swelling, tenderness, organ/joint condition",
            },
          ],
          matters: {
            heading: "Why this matters",
            description: `
            <p>
              Pulse qualities and touch findings provide a
              <span className="highlight">
                real-time map of doshic activity
              </span>
              . They help distinguish whether issues are
              <b>Vata (movement/nerve)</b>,
              <b> Pitta (heat/inflammation)</b>, or
              <b> Kapha (fluid/stability)</b> dominant, so
              treatments can be targeted—
              <b>right herb, right therapy, right intensity</b>.
            </p>
            `,
          },
        },
        {
          step: 5,
          title: "Eightfold Examination (Ashtasthana Pariksha)",
          subtitle: "Step 5. Eightfold Examination (Ashtasthana Pariksha)",
          image: singleImg08,
          collectHead: "What we check",
          collect: [
            {
              label: "Pulse (Nadi)",
              value: "",
            },
            {
              label: "Urine (Mutra):",
              value: "frothy, cloudy, reddish signs",
            },
            {
              label: "Stool (Mala):",
              value: "constipation, diarrhea, stickiness",
            },
            {
              label: "Tongue (Jihva):",
              value: "white coat = Kapha, yellow = Pitta, black/dry = Vata",
            },
            {
              label: "Voice (Shabda):",
              value: "weak, harsh, or strong",
            },
            {
              label: "Touch/Temperature (Sparsha)",
              value: "",
            },
            {
              label: "Eyes (Drik)",
              value: "",
            },
            {
              label: "Body shape (Akruti)",
              value: "",
            },
          ],
          matters: {
            heading: "Why this matters",
            description: `
            <p>
              The <b>Eightfold Examination</b> combines external
              and internal markers to assess
              <span className="highlight">
                dosha balance
              </span>
              and
              <span className="highlight">
                Ama (toxin load)
              </span>
              . For example, tongue coating may reveal
              <b>Kapha Ama</b>, while yellow discoloration
              points to <b>Pitta excess</b>. 👉 By integrating
              all eight signs, the vaidya gains a
              <b>360° diagnostic view</b> of your health.
            </p>
            `,
          },
        },
        {
          step: 6,
          title: "Tenfold Examination (Dashavidha Pariksha)",
          subtitle: "Step 6. Tenfold Examination (Dashavidha Pariksha)",
          image: singleImg08,
          collectHead: "What we map",
          collect: [
            {
              label: "Prakriti:",
              value: "in-born Vata–Pitta–Kapha balance",
            },
            {
              label: "Vikruti:",
              value: "current disturbance/imbalance",
            },
            {
              label: "Sara:",
              value:
                "tissue quality (blood, muscle, fat, bone, marrow, semen/ojas)",
            },
            {
              label: "Samhanana:",
              value: "body build (compact/strong/weak)",
            },
            {
              label: "Pramana:",
              value: "proportions & measurements (height/weight/frames)",
            },
            {
              label: "Satmya:",
              value: "adaptability—foods/regimens that suit you",
            },
            {
              label: "Satva:",
              value: "mental strength—confidence, memory, fears",
            },
            {
              label: "Ahara Shakti:",
              value: "digestive power/appetite & assimilation",
            },
            {
              label: "Vyayama Shakti:",
              value: "exercise capacity/tolerance",
            },
            {
              label: "Vaya:",
              value: "age & life stage considerations",
            },
          ],
          matters: {
            heading: "Why this matters",
            description: `
            <p>
              The tenfold lens creates a
              <span className="highlight">
                precise health profile
              </span>
              that guides the <b>right diet</b> (what to
              favor/avoid), <b>correct therapies</b> and oils,
              <b> dosage & herb selection</b>, and a suitable
              <b>exercise plan</b>. It aligns treatment with
              your <b>constitution, capacity, and life stage</b>
              —making care safer, targeted, and effective.
            </p>
            `,
          },
        },
      ],
      pricing: [],
    },
    {
      id: 2,
      url: "/detoxification-&-rejuvenation-therapies",
      name: "Detoxification & Rejuvenation Therapies",
      image: [Service02],
      serviceListingDesc:
        "Preparatory phase of Panchkarma with Snehana (oleation), Abhyanga (oil massage), Shirodhara, Swedana, and other therapies to loosen toxins, balance doshas, and prepare the body for deep detoxification.",
      description:
        "A structured program to detoxify, balance, and rejuvenate your body and mind.",
      detailedPageTopDescription: "",
      steps: [],
    },
    {
      id: 3,
      url: "/panchkarma",
      name: "Panchkarma",
      image: [Service03],
      serviceListingDesc:
        "Experience classical Ayurvedic therapies including Abhyanga oil massage, Shirodhara, Basti, Nasya, Swedana herbal steam, Udvartana powder massage, Netra Basti, and Marma therapy — designed to balance doshas, relieve stress, detoxify, and rejuvenate body and mind.",
      description:
        "A structured program to detoxify, balance, and rejuvenate your body and mind.",
      detailedPageTopDescription:
        "A structured program to detoxify, balance, and rejuvenate your body and mind.",
      steps: [
        {
          step: 1,
          title: "Registration & Intake (Pravesh)",
          subtitle: "Step 1. Registration & Intake (Pravesh)",
          image: singleImg08, // imported image asset
          collectHead: "What we collect",
          collect: [
            { label: "Basic details", value: "age, gender, occupation" },
            {
              label: "Lifestyle habits",
              value: "diet, sleep, stress, exercise, addictions",
            },
            {
              label: "Health history",
              value: "past illnesses & current treatments",
            },
          ],
          matters: {
            heading: "Why this matters",
            description: `
              <p>
                This gives us a 
                <span class="highlight">complete picture of your health</span> 
                — not just symptoms. It helps identify your 
                <b>natural constitution (Prakriti)</b>, current 
                <b>imbalances (Vikriti)</b>, and guides a plan
                that fits your body, mind, and lifestyle.
              </p>
            `,
          },
        },
        {
          step: 2,
          title: "Primary Discussion (Prashna Pariksha)",
          subtitle: "Step 2. Primary Discussion (Prashna Pariksha)",
          image: singleImg08,
          collectHead: "What we ask",
          collect: [
            {
              label: "Complaints",
              value:
                "what brings you here (pain, digestion, skin, stress, etc.)",
            },
            {
              label: "History",
              value: "when did it start, what worsens/relieves it",
            },
            {
              label: "Family tendencies",
              value: "diabetes, hypertension, allergies, etc.",
            },
            {
              label: "Mental health",
              value: "mood, memory, emotional stability",
            },
          ],
          matters: {
            heading: "Why this matters",
            description: `
              <p>
                This helps us trace the 
                <span class="highlight">root of your health issues</span>,
                not just symptoms. Patterns in history and family tendencies 
                reveal deeper imbalances. <br><br>
                👉 Here we already begin to see <b>dosha tendencies</b>:
                <span class="highlight"> anxiety → Vata</span>,
                <span class="highlight"> anger → Pitta</span>,
                <span class="highlight"> laziness → Kapha</span>.
              </p>
            `,
          },
        },
      ],
    },
    {
      id: 4,
      url: "/yoga-therapy",
      name: "Yoga Therapy",
      image: [Service04],
      serviceListingDesc:
        "A holistic blend of preventive and healing practices in Ayurveda, offering diet and lifestyle planning, yoga therapy, meditation, USHA Kriya, and herbal remedies to nurture balance of body, mind, and spirit.",
      description:
        "A structured program to detoxify, balance, and rejuvenate your body and mind.",
      detailedPageTopDescription: "",
      steps: [],
    },
    {
      id: 5,
      url: "/naturopathy",
      name: "Naturopathy",
      image: [Service05],
      serviceListingDesc:
        "Focused Ayurvedic care programs designed for modern concerns—offering stress and sleep improvement therapies like Shirodhara and meditation, along with pain management through basti, marma therapy, and herbal support.",
      description:
        "A structured program to detoxify, balance, and rejuvenate your body and mind.",
      detailedPageTopDescription: "",
      steps: [],
    },
    {
      id: 6,
      url: "/localized-&-specialty-therapies",
      name: "Localized & Specialty Therapies",
      image: [Service06],
      serviceListingDesc:
        "Experience age-old wellness rooted in Indian traditions—Panchgavya therapies, Desi Vaidya consultations, Agni Chikitsa, Gau Shala retreats, and simple home remedies that nurture immunity, balance, and spiritual harmony.",
      description:
        "A structured program to detoxify, balance, and rejuvenate your body and mind.",
      detailedPageTopDescription: "",
      steps: [],
    },
    {
      id: 7,
      url: "/body-&-sensory-therapies",
      name: "Body & Sensory Therapies",
      image: [Service06],
      serviceListingDesc: "Listing Page",
      description: "Testing",
      detailedPageTopDescription: "Detailed Page Description",
      steps: [],
    },
  ];

  const TreatmentsUrlLink =
    AllTreatmentListing.length > 0
      ? AllTreatmentListing.map((item) => {
          return {
            id: item.id,
            url: item.url,
            name: item.name,
          };
        })
      : null;

  useEffect(() => {
    setServiceLinks(SericesUrlLinks);
    setTreatmentLinks(TreatmentsUrlLink);
    // console.log(getApiBaseUrl());
    if (getApiBaseUrl() == "https://backend.bharatdhara.com/api/") {
      setProduction(true);
    }

    setSideBarComponents({
      Dashboard,
      PatientAppointments,
      Doctors,
      Prescriptions,
      Invoice,
      ProfileSettings,
      ChangePassword,
      NotificationSettings,
    });

    setSideBar([
      {
        name: "Dashboard",
        url: "/dashboard",
        page: "Dashboard",
        icon: "ti ti-layout-dashboard",
      },
      {
        name: "Appointments",
        url: "/patient-appointments",
        page: "PatientAppointments",
        icon: "ti ti-calendar-check",
      },
      {
        name: "Doctors",
        url: "/patient-doctors",
        page: "Doctors",
        icon: "ti ti-stethoscope",
      },
      {
        name: "Prescriptions",
        url: "/patient-prescriptions",
        page: "Prescriptions",
        icon: "ti ti-prescription",
      },
      {
        name: "Invoice",
        url: "/patient-invoices",
        page: "Invoice",
        icon: "ti ti-star",
      },
      {
        name: "Settings",
        icon: "ti ti-settings",
        submenu: [
          {
            name: "Profile Settings",
            url: "/patient-profile-settings",
            page: "ProfileSettings",
          },
          {
            name: "Change Password",
            url: "/patient-password-settings",
            page: "ChangePassword",
          },
          {
            name: "Notifications",
            url: "/patient-notifications-settings",
            page: "NotificationSettings",
          },
        ],
      },
    ]);
  }, []);

  return (
    <Routes>
      {/* Ayurveda theme routes */}
      <Route
        path="/"
        element={
          <MainLayout
            menuWrapperRef={menuWrapperRef}
            menuContentRef={menuContentRef}
            serviceLinks={serviceLinks}
            treatmentLinks={TreatmentLinks}
            production={Production}
          >
            <Home production={Production} ServicesNames={ServicesNames} />
          </MainLayout>
        }
      />
      <Route
        path="/about"
        element={
          <MainLayout
            menuWrapperRef={menuWrapperRef}
            menuContentRef={menuContentRef}
            serviceLinks={serviceLinks}
            treatmentLinks={TreatmentLinks}
            production={Production}
          >
            <About />
          </MainLayout>
        }
      />
      <Route
        path="/services"
        element={
          <MainLayout
            menuWrapperRef={menuWrapperRef}
            menuContentRef={menuContentRef}
            serviceLinks={serviceLinks}
            treatmentLinks={TreatmentLinks}
            production={Production}
          >
            <Services AllServicesListing={AllServicesListing} />
          </MainLayout>
        }
      />

      {/* Dedicated Dhara page */}
      <Route
        path="/Dhara-Wellness-Program"
        element={
          <MainLayout
            menuWrapperRef={menuWrapperRef}
            menuContentRef={menuContentRef}
            serviceLinks={serviceLinks}
            treatmentLinks={TreatmentLinks}
            production={Production}
          >
            <DharaWellnessProgram AllServicesListing={AllServicesListing} />
          </MainLayout>
        }
      />

      {/* Dynamic service detail pages – skip Dhara so it uses its own component */}
      <Route
        path={`/services/:slug`}
        element={
          <MainLayout
            menuWrapperRef={menuWrapperRef}
            menuContentRef={menuContentRef}
            serviceLinks={serviceLinks}
            treatmentLinks={TreatmentLinks}
            production={Production}
          >
            <DetailedService AllServicesListing={AllServicesListing} />
          </MainLayout>
        }
      />
      <Route
        path="/USHAKriya-Wellness"
        element={
          <MainLayout
            menuWrapperRef={menuWrapperRef}
            menuContentRef={menuContentRef}
            serviceLinks={serviceLinks}
            treatmentLinks={TreatmentLinks}
            production={Production}
          >
            <USHAKriyaWellness AllServicesListing={AllServicesListing} />
          </MainLayout>
        }
      />

      <Route
        path={`/treatments/:slug`}
        element={
          <MainLayout
            menuWrapperRef={menuWrapperRef}
            menuContentRef={menuContentRef}
            serviceLinks={serviceLinks}
            treatmentLinks={TreatmentLinks}
            production={Production}
          >
            <DetailedTreatments AllTreatmentListing={AllTreatmentListing} />
          </MainLayout>
        }
      />
      <Route
        path="/centers-and-camps"
        element={
          <MainLayout
            menuWrapperRef={menuWrapperRef}
            menuContentRef={menuContentRef}
            serviceLinks={serviceLinks}
            treatmentLinks={TreatmentLinks}
            production={Production}
          >
            <OurCenters />
          </MainLayout>
        }
      />
      <Route
        path="/team"
        element={
          <MainLayout
            menuWrapperRef={menuWrapperRef}
            menuContentRef={menuContentRef}
            serviceLinks={serviceLinks}
            treatmentLinks={TreatmentLinks}
            production={Production}
          >
            <Team />
          </MainLayout>
        }
      />
      <Route
        path="/blog"
        element={
          <MainLayout
            menuWrapperRef={menuWrapperRef}
            menuContentRef={menuContentRef}
            serviceLinks={serviceLinks}
            treatmentLinks={TreatmentLinks}
            production={Production}
          >
            <Blog />
          </MainLayout>
        }
      />
      <Route
        path="/detailed-blog"
        element={
          <MainLayout
            menuWrapperRef={menuWrapperRef}
            menuContentRef={menuContentRef}
            serviceLinks={serviceLinks}
            treatmentLinks={TreatmentLinks}
            production={Production}
          >
            <DetailedBlog />
          </MainLayout>
        }
      />

      <Route
        path="/contact"
        element={
          <MainLayout
            menuWrapperRef={menuWrapperRef}
            menuContentRef={menuContentRef}
            serviceLinks={serviceLinks}
            treatmentLinks={TreatmentLinks}
            production={Production}
          >
            <Contact />
          </MainLayout>
        }
      />

      <Route
        path="/:maincategory"
        element={
          <MainLayout
            menuWrapperRef={menuWrapperRef}
            menuContentRef={menuContentRef}
            serviceLinks={serviceLinks}
            treatmentLinks={TreatmentLinks}
            production={Production}
          >
            <MainCategory />
          </MainLayout>
        }
      />

      <Route
        path="/:maincategory/:subcategory"
        element={
          <MainLayout
            menuWrapperRef={menuWrapperRef}
            menuContentRef={menuContentRef}
            serviceLinks={serviceLinks}
            treatmentLinks={TreatmentLinks}
            production={Production}
          >
            <SubCategory />
          </MainLayout>
        }
      />

      <Route
        path="/shop"
        element={
          <MainLayout
            menuWrapperRef={menuWrapperRef}
            menuContentRef={menuContentRef}
            serviceLinks={serviceLinks}
            treatmentLinks={TreatmentLinks}
            production={Production}
          >
            <Shop />
          </MainLayout>
        }
      />

      <Route
        path="/product/:slug"
        element={
          <MainLayout
            menuWrapperRef={menuWrapperRef}
            menuContentRef={menuContentRef}
            serviceLinks={serviceLinks}
            treatmentLinks={TreatmentLinks}
            production={Production}
          >
            <ProductDetail />
          </MainLayout>
        }
      />

      <Route
        path="/cart"
        element={
          <MainLayout
            menuWrapperRef={menuWrapperRef}
            menuContentRef={menuContentRef}
            serviceLinks={serviceLinks}
            treatmentLinks={TreatmentLinks}
            production={Production}
          >
            <Cart />
          </MainLayout>
        }
      />
      {/* Another User Yoga Admin Panel */}
      <Route
        path="/register"
        element={
          <PublicRoute>
            <UserPanelLayout production={Production} SideBar={SideBar}>
              <Register />
            </UserPanelLayout>
          </PublicRoute>
        }
      />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <UserPanelLayout production={Production} SideBar={SideBar}>
              <Login />
            </UserPanelLayout>
          </PublicRoute>
        }
      />
      {SideBar.length > 0
        ? SideBar.map((item, index) => {
            if (item.page) {
              const Component = SideBarComponents[item.page];
              return (
                <Route
                  key={item.url}
                  path={item.url}
                  element={
                    <ProtectedRoute>
                      <UserPanelLayout
                        production={Production}
                        SideBar={SideBar}
                      >
                        <Component />
                      </UserPanelLayout>
                    </ProtectedRoute>
                  }
                />
              );
            }

            // SUBMENU ROUTES
            if (item.submenu) {
              return item.submenu.map((sub, subIndex) => {
                if (!sub.page) return null;

                const SubPage = SideBarComponents[sub.page];

                return (
                  <Route
                    key={subIndex}
                    path={sub.url}
                    element={
                      <ProtectedRoute>
                        <UserPanelLayout
                          production={Production}
                          SideBar={SideBar}
                        >
                          <SubPage />
                        </UserPanelLayout>
                      </ProtectedRoute>
                    }
                  />
                );
              });
            }

            return null;
          })
        : null}

      <Route
        path="/detailed-perscription"
        element={
          <UserPanelLayout production={Production} SideBar={SideBar}>
            <DetailedPescription />
          </UserPanelLayout>
        }
      />
      <Route
        path="/detailed-invoices"
        element={
          <UserPanelLayout production={Production} SideBar={SideBar}>
            <DetailedInvoices />
          </UserPanelLayout>
        }
      />
    </Routes>
  );
}

export default App;
